import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { Modal, ModalSize } from "@redotech/redo-web/modal";
import { ProgressBar } from "@redotech/redo-web/progress-bar";
import { memo, useEffect, useState } from "react";
import {
  bulkUpdateConversationsStatus,
  updateConversation,
} from "../client/conversations";
import { getNextBatch } from "../utils";
import * as reopenTicketModalCss from "./reopen-ticket-modal.module.css";

export const ReopenTicketModal = memo(function ReopenTicketModal({
  open,
  setOpen,
  conversations,
  setActiveConversation,
  inDetailView,
  cleanupFn,
  selectAllInfo,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  setActiveConversation: (val: MinimalConversation) => void;
  inDetailView: boolean;
  cleanupFn?: (clearSelected?: boolean) => void;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    bulkActionIterator: AsyncIterator<{ conversations: MinimalConversation[] }>;
    setBlockRefresh: (val: boolean) => void;
  };
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [conversationsCanReopen, setConversationsCanReopen] = useState<
    MinimalConversation[]
  >(conversations.filter((conversation) => conversation.status !== "open"));
  const [reopeningSelectAll, setReopeningSelectAll] = useState(false);
  const [numDone, setNumDone] = useState(0);

  const handleReopenTickets = async () => {
    if (conversations.length === 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      void updateConversation(client, conversations[0], { status: "open" });
      amplitude.logEvent("reopen-conversation", {
        mode: "single",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        conversationIds: [conversations[0]._id],
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        channels: [conversations[0].platform],
      });
      if (inDetailView) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setActiveConversation({ ...conversations[0], status: "open" });
      }
    } else if (conversations.length > 1) {
      const conversationIds = conversations
        .filter((conversation) => conversation.status !== "open")
        .map((conversation) => conversation._id);
      if (conversationIds.length > 0) {
        await bulkUpdateConversationsStatus(client, {
          conversationIds,
          status: "open",
        });
        const channels = [
          ...new Set(
            conversations.map((conversation) => conversation.platform),
          ),
        ];
        amplitude.logEvent("reopen-conversation", {
          mode: "multiple",
          conversationIds,
          channels,
        });
      }
    }
    cleanupFn?.(true);
    setOpen(false);
  };

  const reopenBatch = async (): Promise<{
    batchSize: number;
    done: boolean;
  }> => {
    if (!selectAllInfo) return { batchSize: 0, done: true };
    const { done, value } = await getNextBatch(
      selectAllInfo.bulkActionIterator,
    );
    if (done) {
      return { batchSize: 0, done };
    }
    const conversationIds = value.conversations
      .filter(
        (conversation) =>
          conversation.status !== "open" &&
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ),
      )
      .map((conversation) => conversation._id);
    if (conversationIds.length > 0) {
      await bulkUpdateConversationsStatus(client, {
        conversationIds,
        status: "open",
      });
    }
    return { batchSize: value.conversations.length, done: false };
  };

  // This function synchronously reopens each page of conversations.
  // If speed becomes an issue, we could asynchronously reopen each page.
  const handleReopenSelectAll = async () => {
    if (!selectAllInfo) return;
    setReopeningSelectAll(true);
    selectAllInfo.setBlockRefresh(true);
    let done = false;
    do {
      const result = await reopenBatch();
      setNumDone((prevNumDone) => prevNumDone + result.batchSize);
      done = result.done;
    } while (!done);
    amplitude.logEvent("reopen-conversation", { mode: "all" });
    setReopeningSelectAll(false);
    selectAllInfo.setBlockRefresh(false);
    cleanupFn?.(true);
    setOpen(false);
  };

  useEffect(() => {
    setConversationsCanReopen(
      conversations.filter((conversation) => conversation.status !== "open"),
    );
  }, [conversations]);

  const plural = conversationsCanReopen.length > 1 || selectAllInfo;

  const footer = (
    <div className={reopenTicketModalCss.actionButtonsContainer}>
      <Button onClick={() => setOpen(false)} theme={ButtonTheme.OUTLINED}>
        Cancel
      </Button>
      <Button
        onClick={async () => {
          if (selectAllInfo) {
            await handleReopenSelectAll();
          } else {
            await handleReopenTickets();
          }
        }}
        theme={ButtonTheme.PRIMARY}
      >
        Yes, reopen ticket{plural ? "s" : ""}
      </Button>
    </div>
  );

  return (
    <Modal
      footer={reopeningSelectAll ? undefined : footer}
      onClose={() => {
        cleanupFn?.(true);
        setOpen(false);
      }}
      open={open}
      showHeaderBorder={false}
      size={ModalSize.SMALL}
      title={
        reopeningSelectAll
          ? "Reopening tickets"
          : `Reopen ${
              selectAllInfo
                ? "all selected "
                : plural
                  ? conversationsCanReopen.length + " "
                  : ""
            }ticket${plural ? "s" : ""}?`
      }
    >
      {reopeningSelectAll ? (
        <ProgressBar
          value={(numDone / (selectAllInfo?.count || numDone)) * 100}
        />
      ) : (
        <div className={reopenTicketModalCss.actionModalContentContainer}>
          <div>
            {plural ? "These tickets" : "This ticket"} will show up in your open
            tickets list.
          </div>
        </div>
      )}
    </Modal>
  );
});
